<template>
  <div class="">
    <div class="">
      <div class="mt-2 mb-2" v-if="boardItem.changeSets && boardItem.changeSets.length > 0">
        <button class="btn btn-block btn-info outline-none" @click="toggleBoardItemHistory = !toggleBoardItemHistory">
          <i class="far fa-clock icon-left"></i>
          {{ toggleBoardItemHistory ? 'Hide History' : 'Show History' }}
        </button>
      </div>

      <div v-if="toggleBoardItemHistory === true">
        <div v-for="changes in boardItem.changeSets" :key="changes.id">
          <div v-for="change in changes.variance" :key="change.newValue">
            <div class="change" v-if="shouldDisplayChange(change)">
              <div class="changes-date">
                <div class="badge badge-primary bi-badge">
                  {{ changes.createdDate | formatDateTime }}
                </div>
              </div>

              <div v-if="change.name === changeTypes.Title || change.name === changeTypes.Description">
                <b>{{ changes.author.firstName }}</b>
                {{ ' changed ' }}
                <b>{{ change.name }}</b>
                {{ ' from ' + change.oldValue + ' to ' + change.newValue }}
              </div>

              <div v-if="change.name === changeTypes.BoardItemStatus">
                <b>{{ changes.author.firstName }}</b>
                {{ 'changed ' }}
                <b>{{ change.name }}</b>
                {{ ' from ' }}
                <span
                  class="badge badge-pill"
                  :class="{
                    'badge-primary': change.oldValue === statusType.Planned,
                    'badge-warning': change.oldValue === statusType.InProgress,
                    'badge-success': change.oldValue === statusType.Done,
                    'badge-info': change.oldValue === statusType.Information,
                  }"
                >
                  {{ change.oldValue }}
                </span>
                {{ ' to ' }}
                <span
                  class="badge badge-pill"
                  :class="{
                    'badge-primary': change.newValue === statusType.Planned,
                    'badge-warning': change.newValue === statusType.InProgress,
                    'badge-success': change.newValue === statusType.Done,
                    'badge-info': change.newValue === statusType.Information,
                  }"
                >
                  {{ change.newValue }}
                </span>
              </div>

              <div v-if="change.name === changeTypes.BoardItemList">
                <b>{{ changes.author.firstName }}</b>
                moved
                <span v-if="change.oldValue">
                  from
                  <b>{{ change.oldValue }}</b>
                </span>
                to
                <b>{{ change.newValue }}</b>
              </div>

              <div v-if="change.name === changeTypes.AssignedTo">
                <b>{{ changes.author.firstName }}</b>
                changed
                <b>Assignment</b>
                from
                <b>{{ getUserName(change.oldValue) }}</b>
                to
                <b>{{ getUserName(change.newValue) }}</b>
              </div>

              <div
                v-if="
                  change.name === changeTypes.PlannedDate ||
                  change.name === changeTypes.DueDate ||
                  change.name === changeTypes.ValidFromDate ||
                  change.name === changeTypes.ValidToDate
                "
                class="change"
              >
                <b>{{ changes.author.firstName }}</b>
                changed
                <b>{{ change.name }}</b>

                <span v-if="change.oldValue">
                  from
                  <br />
                  <b>{{ getFormattedDate(change.oldValue) }}</b>
                  to
                  <br />
                  <b>{{ getFormattedDate(change.newValue) }}</b>
                </span>
                <span v-else>
                  to
                  <b>{{ getFormattedDate(change.newValue) }}</b>
                </span>
              </div>

              <div v-if="change.name === changeTypes.BoardItemTypeGuid">
                <b>{{ changes.author.firstName }}</b>
                changed
                <b>Category/Type</b>
                from
                <br />
                <i class="far" :class="getBoardItemType(change.oldValue).boardItemCategory.icon"></i>
                <b>{{ getBoardItemType(change.oldValue).boardItemCategory.name }}</b>
                <br />
                <b>{{ getBoardItemType(change.oldValue).name }}</b>
                <br />
                to
                <br />
                <i class="far" :class="getBoardItemType(change.newValue).boardItemCategory.icon"></i>
                <b>{{ getBoardItemType(change.newValue).boardItemCategory.name }}</b>
                <br />
                <b>{{ getBoardItemType(change.newValue).name }}</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import { IBoardItemDto, BoardItemStatusDto, VarianceDto } from '@/apiclient/apiclient_generated';
  import { UserModule, UserStore } from '@/store/user';
  import { DateTime } from 'luxon';
  import { BoardStore, BoardModule } from '@/store/board';

  @Component({})
  export default class BoardItemHistory extends Vue {
    @Prop()
    boardItem: IBoardItemDto;
    statusType = BoardItemStatusDto;
    private userStore: UserModule = UserStore;
    private boardStore: BoardModule = BoardStore;

    toggleBoardItemHistory = false;

    changeTypes = {
      Title: 'Title',
      Description: 'Description',
      BoardItemStatus: 'BoardItemStatus',
      AssignedTo: 'AssignedTo',
      DueDate: 'DueDate',
      PlannedDate: 'PlannedDate',
      ValidFromDate: 'ValidFromDate',
      ValidToDate: 'ValidToDate',
      BoardItemTypeGuid: 'BoardItemTypeGuid',
      BoardItemList: 'BoardItemList',
    };

    boardItemTypes: any = {};

    private getUserName(userId) {
      const user = this.userStore.users.find((_) => _.id === userId);
      return user ? user.firstName : 'Unassigned';
    }

    private shouldDisplayChange(change: VarianceDto) {
      const condition =
        change.name === this.changeTypes.Title ||
        change.name === this.changeTypes.Description ||
        //
        change.name === this.changeTypes.BoardItemStatus ||
        //
        change.name === this.changeTypes.AssignedTo ||
        //
        change.name === this.changeTypes.PlannedDate ||
        change.name === this.changeTypes.DueDate ||
        change.name === this.changeTypes.ValidFromDate ||
        change.name === this.changeTypes.ValidToDate ||
        //
        change.name === this.changeTypes.BoardItemTypeGuid ||
        //
        change.name === this.changeTypes.BoardItemList;

      return condition;
    }

    private getBoardItemType(typeId) {
      if (this.boardItemTypes[typeId]) {
        return this.boardItemTypes[typeId];
      }

      this.boardItemTypes[typeId] = this.boardStore.boardItemTypes.find((_) => _.id === typeId);

      return this.boardItemTypes[typeId];
    }

    private getFormattedDate(source) {
      if (!source) {
        return 'No date';
      }

      const date = DateTime.fromISO(source);
      return date.toFormat('dd.MM.yyyy HH:mm');
    }
  }
</script>

<style scoped lang="scss">
  .changes-date {
    display: flex;
    justify-content: flex-end;
  }

  .change {
    border-radius: 3px;
    box-shadow: 0 1px 2px -1px rgba(9, 30, 66, 0.25), 0 0 0 1px rgba(9, 30, 66, 0.08);
    margin: 5px 0;
    padding: 5px;
    background: white;

    overflow-wrap: break-word;
  }
  .badge-pill {
    font-size: 0.9rem;
  }
</style>
